import { useEffect, useState } from 'react';
import { Box, CircularProgress, IconButton, Stack, Typography } from '@mui/material';

import { FHChip, FHConfirmDialog, FHInputField } from '../../../../../../components/base';
import DeleteIconBordered from '../../../../../../components/svgs/DeleteIconBordered';
import { DocumentPreviewDialog } from '../../../../../../pages/questionnaires/common';
import { ENTITY_PREFIX, QUESTIONNAIRE_STATUS, Roles } from '../../../../../../constants';
import { areUserRolesIn, formatEntityId } from '../../../../../../helpers';
import { useAuthUser } from '../../../../../../hooks';
import { FORTA_GRAY5 } from '../../../../../../utils/colors';
import {
  DELETE_ONBOARDING_DOCUMENT_PASSWORD,
  DOCUMENT_SUB_TYPE_OPTIONS,
  DOCUMENT_TYPE_OPTIONS,
  QUESTIONNAIRE_CHIP_FONT_SIZE,
  QUESTIONNAIRE_CHIP_WIDTH,
  QUESTIONNAIRE_STATUS_COLORS,
  QUESTIONNAIRE_STATUS_LABELS,
} from '../constants';
import {
  useDeleteOnboardingQuestionnaireDocument,
  usePreviewOnboardingQuestionnaireDocument,
} from '../hooks';
import {
  UPLOADED_DOCUMENT_HEADING_SX,
  UPLOADED_DOCUMENT_LIST_SX,
  UPLOADED_DOCUMENT_META_SX,
  UPLOADED_DOCUMENT_ROW_SX,
} from '../styles';
import { getDocumentOptionLabel } from '../utils';

import type { ChangeEvent, KeyboardEvent, MouseEvent } from 'react';
import type { IOnboardingQuestionnaireDocument } from '../../../../../../pages/questionnaires/onboarding/types';
import type { UploadedOnboardingDocumentsListProps } from '../types';

function UploadedOnboardingDocumentsList({
  documents,
  loading,
  onboardingQuestionnaireId,
  questionnaireStatus,
  onDocumentDeleted,
}: UploadedOnboardingDocumentsListProps) {
  const { authUser } = useAuthUser();
  const { closePreview, openPreview, previewLoadingDocumentId, previewingDocument } =
    usePreviewOnboardingQuestionnaireDocument(onboardingQuestionnaireId);
  const { canDeleteDocument, deleteDocument, deleteDocumentLoading } =
    useDeleteOnboardingQuestionnaireDocument();
  const [pendingDeleteDocument, setPendingDeleteDocument] =
    useState<IOnboardingQuestionnaireDocument | null>(null);
  const [deletePassword, setDeletePassword] = useState('');

  const isDeleteDialogOpen = pendingDeleteDocument != null;
  const isDeletePasswordCorrect = deletePassword === DELETE_ONBOARDING_DOCUMENT_PASSWORD;
  const allowDelete = questionnaireStatus !== QUESTIONNAIRE_STATUS.COMPLETED;
  const showDelete = canDeleteDocument && allowDelete;
  const questionnaireStatusLabel = QUESTIONNAIRE_STATUS_LABELS[questionnaireStatus];

  useEffect(() => {
    if (!isDeleteDialogOpen) {
      setDeletePassword('');
    }
  }, [isDeleteDialogOpen]);

  useEffect(() => {
    if (allowDelete) return;
    setPendingDeleteDocument(null);
    setDeletePassword('');
  }, [allowDelete]);

  const handlePreviewKeyDown = (
    event: KeyboardEvent,
    document: IOnboardingQuestionnaireDocument,
  ) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      void openPreview(document);
    }
  };

  const handleRequestDelete = (event: MouseEvent, document: IOnboardingQuestionnaireDocument) => {
    event.stopPropagation();
    setPendingDeleteDocument(document);
  };

  const handleCancelDelete = () => {
    if (deleteDocumentLoading) return;
    setPendingDeleteDocument(null);
  };

  const handleDeletePasswordChange = (event: ChangeEvent<HTMLInputElement>) => {
    setDeletePassword(event.target.value);
  };

  const handleConfirmDelete = async () => {
    if (pendingDeleteDocument == null || !isDeletePasswordCorrect) return;

    const deleted = await deleteDocument({
      onboardingQuestionnaireId,
      documentId: pendingDeleteDocument.id,
      questionnaireStatus,
    });

    if (deleted) {
      if (previewingDocument?.id === pendingDeleteDocument.id) {
        closePreview();
      }
      onDocumentDeleted(pendingDeleteDocument.id);
      setPendingDeleteDocument(null);
    }
  };

  return (
    <Stack sx={UPLOADED_DOCUMENT_LIST_SX}>
      <Box sx={UPLOADED_DOCUMENT_HEADING_SX}>
        <Typography sx={{ textAlign: 'left', fontWeight: 'bold' }}>
          Uploaded documents (
          {formatEntityId(onboardingQuestionnaireId, ENTITY_PREFIX.OnboardingQuestionnaire)})
        </Typography>
        <FHChip
          content={questionnaireStatusLabel}
          bgColor={QUESTIONNAIRE_STATUS_COLORS[questionnaireStatusLabel]}
          fontSize={QUESTIONNAIRE_CHIP_FONT_SIZE}
          sx={{ width: QUESTIONNAIRE_CHIP_WIDTH, textAlign: 'center' }}
        />
      </Box>
      {loading ? <CircularProgress size={24} /> : null}
      {!loading && documents.length === 0 ? (
        <Typography sx={{ textAlign: 'left' }}>No documents uploaded</Typography>
      ) : null}
      {!loading && documents.length > 0
        ? documents.map((document) => (
            <Box
              key={document.id}
              aria-busy={previewLoadingDocumentId === document.id}
              aria-label={`Preview ${document.title}`}
              onClick={() => {
                void openPreview(document);
              }}
              onKeyDown={(event) => handlePreviewKeyDown(event, document)}
              role="button"
              sx={UPLOADED_DOCUMENT_ROW_SX}
              tabIndex={0}
            >
              <Typography sx={{ textAlign: 'left' }}>{document.title}</Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                {previewLoadingDocumentId === document.id ? (
                  <CircularProgress size={16} />
                ) : (
                  <Typography sx={UPLOADED_DOCUMENT_META_SX}>
                    {getDocumentOptionLabel(DOCUMENT_TYPE_OPTIONS, document.document_type)}
                    {document.document_sub_type
                      ? ` · ${getDocumentOptionLabel(
                          DOCUMENT_SUB_TYPE_OPTIONS,
                          document.document_sub_type,
                        )}`
                      : ''}
                  </Typography>
                )}
                {showDelete ? (
                  <IconButton
                    aria-label={`Delete ${document.title}`}
                    disabled={deleteDocumentLoading}
                    onClick={(event) => handleRequestDelete(event, document)}
                    size="small"
                    sx={{ p: 0 }}
                  >
                    <DeleteIconBordered width={28} height={28} />
                  </IconButton>
                ) : null}
              </Box>
            </Box>
          ))
        : null}

      <FHConfirmDialog
        open={showDelete && isDeleteDialogOpen}
        title="Delete this document?"
        dialogHeight={340}
        contentRenderer={() => (
          <Box sx={{ pt: 1, flexGrow: 1, width: '100%' }}>
            <Typography sx={{ color: FORTA_GRAY5, fontSize: 14, mb: 2 }}>
              This action cannot be undone. Enter the password to confirm.
            </Typography>
            <Typography variant="body2" sx={{ mb: 1 }} textAlign="left">
              Password
            </Typography>
            <FHInputField
              autoComplete="off"
              disabled={deleteDocumentLoading}
              fullWidth
              inputHeight={40}
              onChange={handleDeletePasswordChange}
              placeholder="Enter password"
              type="password"
              value={deletePassword}
            />
          </Box>
        )}
        canceltext="Cancel"
        submittext="Delete"
        onClose={handleCancelDelete}
        onSubmit={handleConfirmDelete}
        submitLoading={deleteDocumentLoading}
        disableSubmitButton={!isDeletePasswordCorrect || deleteDocumentLoading}
        disableCancelButton={deleteDocumentLoading}
      />

      <DocumentPreviewDialog
        documentFileType={previewingDocument?.file_type}
        documentPreviewUrl={previewingDocument?.document_url ?? ''}
        illustrationAlt={previewingDocument?.title ?? ''}
        onClose={closePreview}
        open={previewingDocument != null}
        showDownload={areUserRolesIn(authUser?.roles, [
          Roles.Admin,
          Roles.ClientSuccessManager,
        ])}
      />
    </Stack>
  );
}

export default UploadedOnboardingDocumentsList;
